import { inject } from '@angular/core';
import { CanActivateFn, Router, ActivatedRouteSnapshot } from '@angular/router';
import { AccessService } from '../services/access.service';
import { Auth, user } from '@angular/fire/auth';
import { map, switchMap, take, of, combineLatest } from 'rxjs';

export const roleGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const auth = inject(Auth);
  const accessService = inject(AccessService);
  const pageId = route.data['id'];

  return user(auth).pipe(
    take(1),
    switchMap(currentUser => {
      if (!currentUser) {
        router.navigate(['/login']);
        return of(false);
      }
      return accessService.getUserProfile(currentUser.uid);
    }),
    switchMap(profile => {
      if (!profile || typeof profile !== 'object') return of(false);
      if (!profile.role) return of(false);
      
      // Super Admin Bypass
      if (profile.role.toLowerCase().trim() === 'superadmin') return of(true);

      return combineLatest([
        accessService.getPagePermissions(),
        accessService.getRoles()
      ]).pipe(
        take(1),
        map(([permissions, roles]) => {
          const roleId = profile.roleId;
          const roleStr = profile.role?.toLowerCase().trim();
          const resolvedRole = roles.find(r => 
            (r.id && (r.id === roleId || r.id.toLowerCase() === roleStr)) ||
            (r.name && roleStr && r.name.toLowerCase().trim() === roleStr)
          );

          // Customer Role Invariant: Never has access to ERP system
          if (resolvedRole?.isCustomer) {
            return router.createUrlTree(['/home/unauthorized']);
          }

          // If no page ID is defined for the route, allow access for non-customer roles
          if (!pageId) return true;

          // External Role Invariant: ONLY 'profit-report' and 'daily-report' are permitted
          if (resolvedRole?.isExternal) {
            if (pageId !== 'profit-report' && pageId !== 'daily-report') {
              return router.createUrlTree(['/home/unauthorized']);
            }
          }

          const perm = permissions.find(p => p.id === pageId);
          const normRole = (resolvedRole?.name || profile.role || '').toLowerCase().trim();
          const hasAccess = perm && perm.roles ? perm.roles.some(r => r.toLowerCase().trim() === normRole) : false;
          return hasAccess ? true : router.createUrlTree(['/home/unauthorized']);
        })
      );
    })
  );
};